<template>
    <simple-select
        :value="experimentValue"
        :options="experimentOptions"
        :loading-resources="loadingResources"
        :field="['experimentId', 'experiment', 'experimentConverted', 'variantName']"
        :check-valid-rule="checkValidRule"
        :disabled="readOnly"
        @input="$emit('input', $event)">
        <template #noData>
            <div class="no-data-message">
                {{ noDataText }}
            </div>
        </template>
        <template #option="{ option }">
            <pendo-status-option
                :option
                :status-color-map />
        </template>
        <template #selectedLabel="{ option }">
            <pendo-status-option
                :option
                :status-color-map />
        </template>
    </simple-select>
    <template v-if="experimentValue">
        <template v-if="!isLocaleEnglish">
            <div class="flex-break" />
            <strong>{{ $t('Condition') }}</strong>
            <div class="flex-break" />
        </template>
        <simple-select
            :value="experimentVariantValue"
            :options="experimentVariantOptions"
            field="variantName"
            :check-valid-rule="checkValidRule"
            :disabled="readOnly"
            @input="$emit('input', $event)" />
    </template>
    <template v-if="experimentVariantValue && experimentHasConversion">
        <simple-select
            :value="experimentConversionValue"
            :options="experimentConversionOptions"
            field="experimentConverted"
            :check-valid-rule="checkValidRule"
            :disabled="readOnly"
            preselect-first
            @input="$emit('input', $event)" />
    </template>
</template>

<script>
import SimpleSelect from '@/segments/components/segment-editor-vue/components/simple-select.vue';
import { getNoResourcesFoundText } from '@/segments/components/segment-editor-vue/segment-editor.utils';
import { PendoStatusOption } from '@pendo/components';
import { experimentStatusColorMap } from '@/utils/experiment.utils';
import get from 'lodash/get';

export default {
    name: 'GuideExperimentRule',
    components: {
        SimpleSelect,
        PendoStatusOption,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
        resources: {
            type: Array,
            required: true,
        },
        loadingResources: {
            type: Boolean,
            default: false,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        isMultiApp: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        isLocaleEnglish: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['input'],
    data () {
        return {
            statusColorMap: experimentStatusColorMap,
        };
    },
    computed: {
        noDataText () {
            return getNoResourcesFoundText({ rule: this.rule, isMultiApp: this.isMultiApp });
        },
        experimentOptions () {
            return this.resources.filter(({ state }) => state !== 'draft').map((experiment) => ({
                experimentId: experiment.id,
                name: experiment.name,
                experiment: { // return all experiment fields required for the conversion aggregation
                    variants: experiment.variants,
                    conversion: experiment.conversion,
                    startTime: experiment.startTime,
                    endTime: experiment.endTime,
                    name: experiment.name,
                    id: experiment.id,
                },
                state: experiment.state,
            }));
        },
        experimentValue () {
            return this.experimentOptions?.find(({ experimentId }) => experimentId === this.rule.experimentId);
        },
        experimentVariantOptions () {
            return [
                { name: this.$t('guideExperimentRule.Variant A was seen'), variantName: 'A' },
                { name: this.$t('guideExperimentRule.Variant B was seen'), variantName: 'B' },
            ];
        },
        experimentVariantValue () {
            return this.experimentVariantOptions?.find(({ variantName }) => variantName === this.rule.variantName);
        },
        experimentConversionOptions () {
            return [
                { name: this.$t('guideExperimentRule.with any conversion status'), experimentConverted: 'None' },
                { name: this.$t('guideExperimentRule.and converted'), experimentConverted: 'Yes' },
                { name: this.$t("guideExperimentRule.and didn't convert"), experimentConverted: 'No' },
            ];
        },
        experimentConversionValue () {
            return this.experimentConversionOptions.find(({ experimentConverted }) => experimentConverted === this.rule.experimentConverted);
        },
        experimentHasConversion () {
            return !!get(this.experimentValue, 'experiment.conversion.itemId');
        },
    },
};
</script>

<style scoped lang="less">
.flex-break {
    /* Reliable flex line break - forces next items to new line */
    flex-basis: 100%;
    height: 0;
    width: 0;
    overflow: hidden;
}
</style>
